import Component from '../../js/_Component';

const CNAME = 'c-base-carousel';

/** How far a user must drag on an item for it to visually flip to another item while dragging. */
const DRAG_CUTOFF = 0.5;

/** How far a user must drag on an item for it to flip to another item after releasing. */
const SWIPE_CUTOFF = 0.25;

export default class BaseCarousel extends Component {
	/**
	 * Element to place created items within.
	 */
	private itemContainer: HTMLElement;

	/**
	 * An array of all items added to the carousel.
	 */
	private items: HTMLElement[] = [];

	/**
	 * The optional element containing dots representing each item.
	 */
	private dotsContainer: HTMLElement;

	/**
	 * The optional element containing button controls.
	 */
	private buttonsContainer: HTMLElement;

	/**
	 * Dot controls for the items.
	 */
	private dots: HTMLElement[] = [];

	/**
	 * The current item displayed first within the carousel.
	 */
	private currentItem = 0;

	/**
	 * The previously displayed item during the last arrangement pass.
	 */
	private prevArrangementItem = -1; // Set to -1 to ensure first pass performs rearrangement.

	/**
	 * The number of items that appear to the left of the displayed items.
	 */
	private infiniteArrangementFactor = 0;

	/**
	 * How many items are displayed within the bounds of the carousel.
	 */
	private itemsDisplayed = 1;

	/**
	 * Whether or not items are rearranged/duplicated to give the appearance
	 * of infintie scrolling.
	 */
	private infinite = false;

	/**
	 * The X position of the current drag location.
	 */
	private dragX: number = null;

	/**
	 * The Y position of the current drag location.
	 */
	private dragY: number = null;

	/**
	 * The previous X position of the drag location.
	 */
	private lastX: number = null;

	/**
	 * The previous Y position of the drag location.
	 */
	private lastY: number = null;

	/**
	 * Whether or not the most recent drag is using touch.
	 */
	private usingTouch = false;

	/**
	 * The timeout ID used for automatic advancement.
	 */
	private automaticTimeout = 0;

	/**
	 * The timer used for automatic advancement.
	 */
	private automaticTime = 0;

	constructor(element: HTMLElement) {
		super(element);

		const interactionContainer = this.element.querySelector(`.${CNAME}--items`) as HTMLElement;

		this.itemContainer = this.element.querySelector(`.${CNAME}--item-offset`);
		this.dotsContainer = this.element.querySelector(`.${CNAME}--dots`);
		this.buttonsContainer = this.element.querySelector(`.${CNAME}--buttons`);

		this.infinite = this.element.classList.contains('m-infinite');

		// Start up automatic advancement, if provided.
		if (this.element.dataset.automatic) {
			this.setAutomaticAdvancement(parseFloat(this.element.dataset.automatic));
		}

		// Set starting number of items to display, if provided.
		if (this.element.dataset.display) {
			this.setItemsDisplayed(parseInt(this.element.dataset.display));
		}

		// Detect focus within carousel items, and bring them into view, for keyboard navigaton.
		this.element.addEventListener('focusin', (e) => {
			const item = (e.target as HTMLElement).closest(`.${CNAME}--item`) as HTMLElement;
			const index = this.items.indexOf(item);
			if (index >= 0) {
				this.goToItem(index);
			}
		});

		// Detect drag actions starting with the mouse.
		interactionContainer.addEventListener('mousedown', (e) => {
			e.preventDefault();
			if (this.items.length <= 1) {
				return;
			}
			this.beginDrag(e, e.clientX, e.clientY);
			this.usingTouch = false;
		});

		// Detect drag actions starting with touch.
		interactionContainer.addEventListener('touchstart', (e) => {
			if (this.items.length <= 1) {
				return;
			}
			this.beginDrag(e, e.touches[0].clientX, e.touches[0].clientY);
			this.usingTouch = true;
		});

		// Intercept clicking after a drag event to avoid opening links with the same action.
		interactionContainer.addEventListener('click', (e) => this.handleClick(e), true);

		// Allow left/right arrows to change the slide when focused on any dot.
		if (this.dotsContainer) {
			this.dotsContainer.addEventListener('keydown', (event) => {
				if (event.key === 'ArrowLeft') {
					this.goToItem(this.currentItem - 1);
					this.dots[this.currentItem].focus();
				} else if (event.key === 'ArrowRight') {
					this.goToItem(this.currentItem + 1);
					this.dots[this.currentItem].focus();
				}
			});
		}

		// Allow buttons to page through the carousel slides.
		if (this.buttonsContainer) {
			this.buttonsContainer.querySelector(`.${CNAME}--button.m-prev`).addEventListener('click', () => this.prevItem());
			this.buttonsContainer.querySelector(`.${CNAME}--button.m-next`).addEventListener('click', () => this.nextItem());
		}

		// Detect being in and out of view in order to start/pause the automatic timer.
		const observer = new IntersectionObserver((e) => {
			if (e[0].isIntersecting) {
				this.resetAutomaticTimer();
			} else {
				this.stopAutomaticTimer();
			}
		});
		observer.observe(this.element);

		// Get existing items within the markup on initialization, and add them as items.
		Array.from(this.itemContainer.children).forEach((item) => {
			this.addItem(item as HTMLElement);
		});

		// Set initial transform and other side-effects of updating.
		this.updateVisualState();
	}

	/**
	 * Enables or disables automatic advancement with the specified timer, in seconds.
	 * A value of 0 stops automatic advancement.
	 */
	setAutomaticAdvancement(time: number) {
		this.automaticTime = time;
		this.stopAutomaticTimer();
		if (this.isVisible()) {
			this.resetAutomaticTimer();
		}
	}

	/**
	 * Sets the number of items to be displayed within the carousel.
	 */
	setItemsDisplayed(amount: number) {
		if (this.itemsDisplayed === amount) {
			return;
		}
		this.itemsDisplayed = amount;
		this.items.forEach((item) => {
			item.style.width = `${100 / this.itemsDisplayed}%`;
		});

		// Explicitly force resetting the arrangement of items.
		this.updateInfiniteArrangementFactor();
		this.prevArrangementItem = -1;
		this.updateVisualState();
	}

	/**
	 * Retrieves how many items are displayed in the carousel.
	 */
	getItemsDisplayed() {
		return this.itemsDisplayed;
	}

	/**
	 * Retrieves the index of the first displayed item.
	 */
	getCurrentItem() {
		return this.currentItem;
	}

	/**
	 * Retrieves the number of items in the carousel.
	 */
	getItemCount() {
		return this.items.length;
	}

	/**
	 * Retrieves the dots container, so that the dots may potentially be
	 * moved to another location by a managing component.
	 */
	getDotsContainer() {
		return this.dotsContainer;
	}

	/**
	 * Retrieves the button container, so that the buttons may potentially be
	 * moved to another location by a managing component.
	 */
	getButtonsContainer() {
		return this.buttonsContainer;
	}

	/**
	 * Dynamically add a new item to the carousel.
	 * @param item The element to add.
	 */
	addItem(item: HTMLElement) {
		const newItem = document.createElement('div');
		newItem.classList.add(`${CNAME}--item`);
		newItem.style.width = `${100 / this.itemsDisplayed}%`;
		newItem.appendChild(item);
		this.items.push(newItem);
		this.itemContainer.appendChild(newItem);
		if (this.dotsContainer) {
			const dot = document.createElement('button');
			const dotIndex = this.dots.length;

			// Set classes and attributes
			dot.setAttribute('tabindex', dotIndex === 0 ? '0' : '-1');
			dot.setAttribute('aria-label', `Go to slide ${dotIndex + 1}`);
			dot.classList.add(`${CNAME}--dot`);
			if (dotIndex === 0) {
				dot.classList.add('m-active');
			}

			// Set event listeners
			dot.addEventListener('click', () => this.goToItem(dotIndex));

			// Add dot to the array and the DOM
			this.dots.push(dot);
			this.dotsContainer.appendChild(dot);
		}

		// Unhide any available controls if there is more than one item.
		if (this.items.length > 1) {
			this.element.classList.remove('m-controls-hidden');
		}

		// Explicitly force resetting the arrangement of items.
		this.updateInfiniteArrangementFactor();
		this.prevArrangementItem = -1;
		this.updateVisualState();
	}

	/**
	 * Moves the carousel to the next item.
	 */
	nextItem() {
		this.stopAutomaticTimer();
		this.currentItem = this.clampItemIndex(this.currentItem + 1);
		this.element.dispatchEvent(new CustomEvent('carouselitemchanged'));
		this.deferSlideAnimation(1);
	}

	/**
	 * Moves the carousel to the previous item.
	 */
	prevItem() {
		this.stopAutomaticTimer();
		this.currentItem = this.clampItemIndex(this.currentItem - 1);
		this.element.dispatchEvent(new CustomEvent('carouselitemchanged'));
		this.deferSlideAnimation(-1);
	}

	/**
	 * Scrolls a specific item into view on the carousel.
	 * @param index The index of the item to scroll into view.
	 */
	goToItem(index) {
		this.stopAutomaticTimer();
		let distanceMoved = 0;
		if (this.infinite) {
			// Determine the direction the carousel will move in to reach the provided item.
			// Where the item is located within the loop, assuming the leftmost loop item is 0.
			const visualItemIndex = this.clampItemIndex(
				index - this.prevArrangementItem + this.infiniteArrangementFactor,
			);
			// Moving left (to a lower visual index).
			if (visualItemIndex < this.infiniteArrangementFactor) {
				// How far, compared to the visual index of the current item.
				distanceMoved = visualItemIndex - this.infiniteArrangementFactor;
			// Moving right (to a higher visual index).
			} else if (visualItemIndex >= this.infiniteArrangementFactor + this.itemsDisplayed) {
				// How far, compared to the visual index of the last displayed item.
				distanceMoved = visualItemIndex
					- (this.infiniteArrangementFactor + this.itemsDisplayed - 1);
			}
			this.currentItem = this.clampItemIndex(this.currentItem + distanceMoved);
		} else {
			// Smallest index necessary to display the required item on the right.
			const rightIndex = this.clampItemIndex(index - this.itemsDisplayed + 1);
			// eslint-disable-next-line no-param-reassign
			index = this.clampItemIndex(index);

			// Destination item is further to the left than the first displayed item.
			if (index < this.currentItem) {
				distanceMoved = index - this.currentItem;
				this.currentItem = index;
			}

			// Destination item is further to the right than the last displayed item.
			if (this.currentItem < rightIndex) {
				distanceMoved = rightIndex - this.currentItem;
				this.currentItem = rightIndex;
			}
		}
		this.deferSlideAnimation(distanceMoved);
		this.element.dispatchEvent(new CustomEvent('carouselitemchanged'));
	}

	/**
	 * Update the transform applied to the carousel container based on the
	 * current item and number of displayed items.
	 */
	private updateVisualState() {
		if (this.items.length === 0) {
			return;
		}

		this.updateArrangement(this.currentItem);
		this.updateMarks(this.currentItem);
		this.updateTranslation(0);
	}

	/**
	 * Ensures a provided value fits within the allowed item bounds, taking into
	 * consideration looping behavior of the carousel.
	 */
	private clampItemIndex(item: number) {
		if (this.infinite) {
			return ((item % this.items.length) + this.items.length) % this.items.length;
		}

		if (item < 0) {
			return 0;
		}

		const maxIndex = this.items.length - this.itemsDisplayed;

		if (item > maxIndex) {
			return Math.max(maxIndex, 0);
		}

		return item;
	}

	/**
	 * Rearranges internal items based on a focused item.
	 * @param relativeItem The intended 'currentItem' that items are rearranged relative to.
	 */
	private updateArrangement(relativeItem: number) {
		// Perform item rearrangement if the current item has changed.
		if (this.infinite && (relativeItem !== this.prevArrangementItem)) {
			this.prevArrangementItem = relativeItem;
			for (let i = 0; i < this.items.length; i += 1) {
				const index = this.clampItemIndex(relativeItem - this.infiniteArrangementFactor + i);
				const item = this.items[index];
				this.itemContainer.appendChild(item);
			}
		}
	}

	private updateInfiniteArrangementFactor() {
		this.infiniteArrangementFactor = Math.floor((this.items.length - this.itemsDisplayed) / 2);
	}

	/**
	 * Update class marks added to elements based on the position.
	 * @param relativeItem The intended 'currentItem' that classes will be updated relative to.
	 */
	private updateMarks(relativeItem: number) {
		// eslint-disable-next-line no-param-reassign
		relativeItem = this.clampItemIndex(relativeItem);
		let visualLeftItem = relativeItem; // Leftmost item in the displayed set.
		// Infinite scrolling should rotate/overflow indexes to match visual arrangement.
		if (this.infinite) {
			visualLeftItem = this.infiniteArrangementFactor;
		}
		// Item _after_ rightmost item in displayed set.
		const visualRightItem = visualLeftItem + this.itemsDisplayed;
		// Mark items with classes based on their state.
		this.items.forEach((item, index) => {
			// Mark all as inactive first.
			if (this.dotsContainer) {
				this.dots[index].classList.remove('m-active');
				this.dots[index].setAttribute('tabindex', '-1');
			}
			item.classList.remove('m-out', 'm-out-left', 'm-out-right');

			let visualItemIndex = index; // The current item's index relative to the visual arrangement.
			if (this.infinite) {
				visualItemIndex = this.clampItemIndex(
					index - relativeItem + this.infiniteArrangementFactor,
				);
			}
			// Item is arranged to the left of the current item(s).
			if (visualItemIndex < visualLeftItem) {
				item.classList.add('m-out', 'm-out-left');
			// Item is arranged to the right of the current item(s).
			} else if (visualItemIndex >= visualRightItem) {
				item.classList.add('m-out', 'm-out-right');
			// Item is visible.
			} else if (this.dotsContainer) {
				this.dots[index].classList.add('m-active');
				// Only the dot representing the current item (first) is tabbable.
				if (index === relativeItem) {
					this.dots[index].setAttribute('tabindex', '0');
				}
			}
		});
		if (this.buttonsContainer && !this.infinite) {
			const prevButton = this.buttonsContainer.querySelector(`.${CNAME}--button.m-prev`);
			const nextButton = this.buttonsContainer.querySelector(`.${CNAME}--button.m-next`);

			prevButton.classList.remove('m-disabled');
			nextButton.classList.remove('m-disabled');
			if (relativeItem === 0) {
				prevButton.classList.add('m-disabled');
			}
			if (relativeItem === (this.items.length - 1)) {
				nextButton.classList.add('m-disabled');
			}
		}
	}

	private beginDrag(_event: Event, x: number, y: number) {
		this.stopAutomaticTimer();
		this.element.classList.remove('m-inactive');
		this.lastX = x;
		this.lastY = y;
		this.dragX = x;
		this.dragY = y;
		const continueDragMouse = (e: MouseEvent) => (
			this.continueDrag(e, e.clientX, e.clientY)
		);
		const continueDragTouch = (e: TouchEvent) => (
			this.continueDrag(e, e.touches[0].clientX, e.touches[0].clientY)
		);
		const endDragMouse = (e: MouseEvent) => (
			this.endDrag(e)
		);
		const endDragTouch = (e: TouchEvent) => (
			this.endDrag(e)
		);
		const cancelDragTouch = (e: TouchEvent) => (
			this.endDrag(e, true)
		);
		const clearEvents = () => {
			document.body.removeEventListener('mousemove', continueDragMouse);
			document.body.removeEventListener('touchmove', continueDragTouch);
			document.body.removeEventListener('mouseup', endDragMouse, true);
			document.body.removeEventListener('mouseleave', endDragMouse);
			document.body.removeEventListener('touchend', endDragTouch, true);
			document.body.removeEventListener('touchcancel', cancelDragTouch);
			this.element.removeEventListener('carouseldragend', clearEvents);
		};

		this.element.addEventListener('carouseldragend', clearEvents);
		document.body.addEventListener('mousemove', continueDragMouse);
		document.body.addEventListener('touchmove', continueDragTouch);
		document.body.addEventListener('mouseup', endDragMouse, true);
		document.body.addEventListener('mouseleave', endDragMouse);
		document.body.addEventListener('touchend', endDragTouch, true);
		document.body.addEventListener('touchcancel', cancelDragTouch);
	}

	private continueDrag(_e: Event, x: number, y: number) {
		// Store drag coordinates to determine change.
		this.lastX = x;
		this.lastY = y;

		// Determine number of slides dragged past, for updating dots/arrangement.
		const distanceFactor = this.calculateDistanceFactor(x - this.dragX, DRAG_CUTOFF);

		// Determine the index of the item dragged to from the distance factor.
		const itemIndex = this.clampItemIndex(this.currentItem + distanceFactor);

		this.updateArrangement(itemIndex);
		this.updateMarks(itemIndex);
		this.updateTranslation(distanceFactor, x - this.dragX);
	}

	private endDrag(e: Event, ignoreClick: boolean = false) {
		e.preventDefault();
		const delta = this.lastX - this.dragX;
		const distanceFactor = this.calculateDistanceFactor(delta, SWIPE_CUTOFF);
		this.currentItem = this.clampItemIndex(this.currentItem + distanceFactor);

		this.element.dispatchEvent(new CustomEvent('carouseldragend'));
		this.element.dispatchEvent(new CustomEvent('carouselitemchanged'));

		// Make sure the click event generated by a touch is ignored.
		if (this.usingTouch && !ignoreClick) {
			(e.target as HTMLElement).click();
		}

		this.deferSlideAnimation(distanceFactor, delta);
	}

	private handleClick(e: Event) {
		// Use a larger distance tolerance for touch input.
		const distanceTolerance = this.usingTouch ? 6 : 1;

		// Distance dragged is more than tolerance (manhattan distance).
		if (
			(Math.abs(this.lastX - this.dragX) + Math.abs(this.lastY - this.dragY))
			> distanceTolerance
		) {
			e.preventDefault();
			return false;
		}

		return true;
	}

	/**
	 * Updates the visual transform of the carousel based on a provided item and offset.
	 * @param delta How many items left or right the translation should be relative to compared
	 * to the current item.
	 * @param offset The optional offset to use for dragged transforms, otherwise snaps to the
	 * provided item.
	 */
	private updateTranslation(delta: number, offset: number = 0) {
		if (this.infinite) {
			this.itemContainer.style.transform = `translate(${(this.infiniteArrangementFactor * -100 + delta * 100) / this.itemsDisplayed}%, 0) translate(${offset}px, 0)`;
		} else {
			this.itemContainer.style.transform = `translate(${this.currentItem * -100 / this.itemsDisplayed}%, 0) translate(${offset}px, 0)`;
		}
	}

	/**
	 * Determines, given a drag distance, what the modification would be to the current item.
	 * @param distance The distance dragged, in pixels.
	 * @param cutoff The minimum distance that must be traveled, in items, for the factor to change
	 * from 0.
	 * @returns How far left (negatives) or right (positives) the current item would be moved to
	 * match the dragged distance.
	 */
	private calculateDistanceFactor(distance: number, cutoff: number): number {
		// Determine the width of a single item.
		const itemWidth = this.itemContainer.clientWidth / this.itemsDisplayed;
		// Determine the distance dragged, in items.
		const itemDistance = distance / itemWidth;
		if (Math.abs(itemDistance) > cutoff) { // Dragged far enough to change position.
			// Note that dragging to negative direction results in a positive number, and vice versa.
			// Round away from zero (ceil if above, floor if below).
			return Math.ceil(Math.abs(itemDistance) - cutoff) * -Math.sign(itemDistance);
		}
		return 0; // Not far enough, no factor change.
	}

	/**
	 * Performs adjustments necessary to animate changes to the current item, depending on the
	 * carousel's current state.
	 * @param delta How many slides from the left or right the animation should come from.
	 * @param offset The optional offset to use for dragged transforms, otherwise snaps to the
	 * provided item.
	 */
	private deferSlideAnimation(delta: number, offset: number = 0) {
		if (this.infinite) {
			requestAnimationFrame(() => {
				this.element.classList.remove('m-inactive');
				this.updateArrangement(this.currentItem);
				this.updateTranslation(delta, offset);

				requestAnimationFrame(() => {
					this.element.classList.add('m-inactive');
					this.updateVisualState();
				});
			});
		} else {
			this.element.classList.add('m-inactive');
			this.updateVisualState();
		}
	}

	/**
	 * Performs automatic advancement of the carousel.
	 */
	private advanceAutomatic() {
		// Don't advance if the carousel isn't in view or if there aren't enough items.
		if (this.isVisible() && this.items.length > 1) {
			if (this.infinite) {
				this.nextItem();
			} else {
				if (this.getCurrentItem() >= this.getItemCount() - 1) {
					this.goToItem(0);
				} else {
					this.nextItem();
				}
			}
			this.resetAutomaticTimer();
		}
	}

	/**
	 * Stops the timer waiting for the next advancement.
	 */
	private stopAutomaticTimer() {
		if (this.automaticTimeout) {
			clearTimeout(this.automaticTimeout);
		}
	}

	/**
	 * Starts the timer waiting for the next advancement.
	 */
	private resetAutomaticTimer() {
		if (this.automaticTime) {
			this.automaticTimeout = window.setTimeout(
				() => this.advanceAutomatic(),
				this.automaticTime * 1000,
			);
		}
	}

	/**
	 * Determines if the underlying element is in view.
	 */
	private isVisible() {
		const rect = this.element.getBoundingClientRect();
		return !(rect.top >= window.innerHeight || rect.bottom <= 0);
	}
}

Component.register(BaseCarousel, 'BaseCarousel');
